import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  DollarSign,
  Building,
  Calendar,
  Upload,
  PlusCircle,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Filter,
  CreditCard,
  Clock,
  X,
  BarChart3
} from 'lucide-react';

const ModalUploadPlotsExpenseForm = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const [companyID] = useState(1);
  const [businessLocationOptions, setBusinessLocationOptions] = useState([]);
  const [expensesOptions, setExpensesOptions] = useState([]);
  const [formData, setFormData] = useState({
    business_locationIDExpensePlots: '',
    expensePlots: '',
    monthExpensePlots: '',
    yearExpensePlots: '',
    expenseAmountPlots: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [currentYear] = useState(new Date().getFullYear());
  const years = Array.from({ length: 10 }, (_, i) => currentYear + i);

  useEffect(() => {
    fetchBusinessLocations();
    fetchExpenses();
  }, []);

  useEffect(() => {
    if (formData.expensePlots) {
      const expense = expensesOptions.find(e => e.expensesID === formData.expensePlots);
      setSelectedExpense(expense);
    }
  }, [formData.expensePlots, expensesOptions]);

  const fetchBusinessLocations = async () => {
    const formDataToSend = new FormData();
    formDataToSend.append("apiType", 'getBusinessLocationPlotsOnUploadForm');
    formDataToSend.append("companyID", window.userCompanyID || companyID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formDataToSend);
      setBusinessLocationOptions(response.data);
    } catch (error) {
      console.error("Error fetching business locations:", error);
    }
  };

  const fetchExpenses = async () => {
    const formDataToSend = new FormData();
    formDataToSend.append("apiType", 'getBusinessLocationExpensesOnUploadForm');
    formDataToSend.append("companyID", window.userCompanyID || companyID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formDataToSend);
      setExpensesOptions(response.data);
    } catch (error) {
      console.error("Error fetching expenses:", error);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    const { business_locationIDExpensePlots, expensePlots, monthExpensePlots, yearExpensePlots, expenseAmountPlots } = formData;
    
    if (!business_locationIDExpensePlots || !expensePlots || !monthExpensePlots || !yearExpensePlots || !expenseAmountPlots) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Please fill all required fields.' 
      });
      setIsLoading(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('business_locationIDExpensePlots', business_locationIDExpensePlots);
    formDataToSend.append('companyIDExpensePlots', companyID);
    formDataToSend.append('expensePlots', expensePlots);
    formDataToSend.append('monthExpensePlots', monthExpensePlots);
    formDataToSend.append('yearExpensePlots', yearExpensePlots);
    formDataToSend.append('expenseAmountPlots', expenseAmountPlots);
    formDataToSend.append('apiType', 'uploadExpensesPlots');

    try {
      const response = await axios.post(`${server_app_api_php}/upload.php`, formDataToSend);
      
      setSubmitStatus({ 
        type: 'success', 
        message: 'Expense uploaded successfully!' 
      });
      
      // Add to recent expenses
      const selectedLocation = businessLocationOptions.find(loc => 
        loc.businessLocationID === business_locationIDExpensePlots
      );
      const expenseName = expensesOptions.find(exp => 
        exp.expensesID === expensePlots
      );
      
      setRecentExpenses(prev => [{
        id: Date.now(),
        name: expenseName?.expenses || 'Expense',
        location: selectedLocation?.businessLocationName || 'Unknown',
        month: monthExpensePlots,
        year: yearExpensePlots,
        amount: parseFloat(expenseAmountPlots),
        date: new Date().toLocaleDateString()
      }, ...prev.slice(0, 4)]);
      
      // Reset form
      setFormData({
        business_locationIDExpensePlots: '',
        expensePlots: '',
        monthExpensePlots: '',
        yearExpensePlots: '',
        expenseAmountPlots: '',
      });
      setSelectedExpense(null);
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'There seems to be a problem with your internet connectivity. If this problem continues, kindly contact the support team.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  const calculateTotalExpenses = () => {
    return recentExpenses.reduce((total, expense) => total + expense.amount, 0);
  };

  const isFormValid = formData.business_locationIDExpensePlots && 
    formData.expensePlots && formData.monthExpensePlots && 
    formData.yearExpensePlots && formData.expenseAmountPlots;

  return (
    <>
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 pl-64">
        <div className="p-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Plot Expenses</h1>
                <p className="text-gray-600 mt-2">Record and manage expenses for your plots</p>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Locations</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{businessLocationOptions.length}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Building className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Expense Categories</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{expensesOptions.length}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-green-500" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Recent Expenses</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{recentExpenses.length}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <CreditCard className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(calculateTotalExpenses())}</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <DollarSign className="w-6 h-6 text-red-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Expense Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Form Header */}
                  <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center space-x-3">
                      <PlusCircle className="w-6 h-6 text-blue-500" />
                      <h2 className="text-xl font-bold text-gray-900">New Plot Expense</h2>
                    </div>
                    <p className="text-gray-600 mt-2">Record a new expense for plot management</p>
                  </div>

                  {/* Form Content */}
                  <div className="p-8">
                    {submitStatus.message && (
                      <div className={`mb-6 p-4 rounded-xl ${
                        submitStatus.type === 'success' 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center">
                          {submitStatus.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                          )}
                          <p className={submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                            {submitStatus.message}
                          </p>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Business Location */}
                        <div className="md:col-span-2">
                          <label htmlFor="business_locationIDExpensePlots" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <Building className="w-4 h-4 mr-2 text-gray-400" />
                              Business Location *
                            </div>
                          </label>
                          <select
                            id="business_locationIDExpensePlots"
                            value={formData.business_locationIDExpensePlots}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            required
                          >
                            <option value="">Select location...</option>
                            {businessLocationOptions.map((option) => (
                              <option key={option.businessLocationID} value={option.businessLocationID}>
                                {option.businessLocationName}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Expense Category */}
                        {formData.business_locationIDExpensePlots && (
                          <div className="md:col-span-2">
                            <label htmlFor="expensePlots" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <TrendingUp className="w-4 h-4 mr-2 text-gray-400" />
                                Expense Category *
                              </div>
                            </label>
                            <select
                              id="expensePlots"
                              value={formData.expensePlots}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            >
                              <option value="">Select expense...</option>
                              {expensesOptions.map((option) => (
                                <option key={option.expensesID} value={option.expensesID}>
                                  {option.expenses}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Selected Expense Details */}
                        {selectedExpense && (
                          <div className="md:col-span-2 bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-gray-600">Expense Type</p>
                                <p className="font-medium text-gray-900">{selectedExpense.expenses}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">Business Type</p>
                                <p className="font-medium text-blue-600">{selectedExpense.businessType || 'Plots'}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Month */}
                        {formData.expensePlots && (
                          <div>
                            <label htmlFor="monthExpensePlots" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                Month *
                              </div>
                            </label>
                            <select
                              id="monthExpensePlots"
                              value={formData.monthExpensePlots}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            >
                              <option value="">Select month...</option>
                              {["January", "February", "March", "April", "May", "June", 
                                "July", "August", "September", "October", "November", "December"].map((month) => (
                                <option key={month} value={month}>{month}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Year */}
                        {formData.monthExpensePlots && (
                          <div>
                            <label htmlFor="yearExpensePlots" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                Year *
                              </div>
                            </label>
                            <select
                              id="yearExpensePlots"
                              value={formData.yearExpensePlots}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            >
                              <option value="">Select year...</option>
                              {years.map((year) => (
                                <option key={year} value={year}>{year}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Amount */}
                        {formData.yearExpensePlots && (
                          <div className="md:col-span-2">
                            <label htmlFor="expenseAmountPlots" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
                                Amount (ZMW) *
                              </div>
                            </label>
                            <input
                              type="number"
                              id="expenseAmountPlots"
                              value={formData.expenseAmountPlots}
                              onChange={handleInputChange}
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>
                        )}
                      </div>

                      {/* Submit Button */}
                      <div className="pt-6">
                        <button
                          type="submit"
                          disabled={!isFormValid || isLoading}
                          className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                            isFormValid && !isLoading
                              ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isLoading ? (
                            <div className="flex items-center justify-center">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                              Uploading Expense...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <Upload className="w-5 h-5 mr-2" />
                              Record Expense
                            </div>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Form Footer */}
                  <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Financial Records</p>
                          <p className="text-xs text-gray-500">All expenses are recorded for accounting purposes</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">5 required fields</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Expenses & Guidelines */}
              <div className="space-y-8">
                {/* Recent Expenses */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-blue-500" />
                      Recent Expenses
                    </h3>
                  </div>
                  <div className="p-6">
                    {recentExpenses.length > 0 ? (
                      <div className="space-y-4">
                        {recentExpenses.map(exp => (
                          <div key={exp.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">{exp.name}</p>
                              <p className="text-xs text-gray-500">{exp.location}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-red-600">{formatCurrency(exp.amount)}</p>
                              <p className="text-xs text-gray-500">
                                {exp.month} {exp.year}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">Total</span>
                            <span className="font-bold text-red-600">{formatCurrency(calculateTotalExpenses())}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No recent expenses</p>
                        <p className="text-sm text-gray-400 mt-1">Record your first expense above</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expense Guidelines */}
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
                    Expense Guidelines
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Record all plot-related operational expenses</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Use proper expense categories for better tracking</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Keep receipts and documentation for all expenses</span>
                    </li>
                  </ul>
                </div>

                {/* Expense Categories */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <BarChart3 className="w-5 h-5 mr-2 text-purple-500" />
                      Expense Categories
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      {expensesOptions.slice(0, 5).map(expense => (
                        <div key={expense.expensesID} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">{expense.expenses}</span>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                            {expense.businessType || 'Plots'}
                          </span>
                        </div>
                      ))}
                      {expensesOptions.length > 5 && (
                        <p className="text-sm text-gray-500 text-center">
                          +{expensesOptions.length - 5} more categories
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalUploadPlotsExpenseForm;